import { usePage, Link, router } from "@inertiajs/react"
import { LogOut } from "lucide-react"
import { LogoMark } from "./logo-mark"
import { CompanySwitcher } from "./company-switcher"
import { navigation, type NavItem } from "@/config/navigation"
import { useCan } from "@/lib/authz"
import { cn } from "@/lib/cn"
import type { SharedProps } from "@/types/inertia"

type TicketsNavQueue = { id: number; name: string; count: number }
type TicketsNav = { total: number; mine: number; queues: TicketsNavQueue[] }

export function Sidebar() {
  const page = usePage<SharedProps & { ticketsNav?: TicketsNav }>()
  const { url } = page
  const ticketsNav = page.props.ticketsNav
  const can = useCan()

  // Inject dynamic children under the Tickets group from `ticketsNav`.
  // Empty queue list still leaves All + My as a useful split. Counts =
  // null collapses the parent (no escalated convos at all).
  // Then filter the whole tree by capability — items with a
  // `requiresCapability` the viewer lacks are dropped, and parent groups
  // collapse only when every one of their children also collapsed.
  const items: NavItem[] = navigation
    .map((item) => {
      if (item.label !== "Tickets" || !ticketsNav) return item
      const children: NavItem[] = [
        { label: "All", href: "/tickets", count: ticketsNav.total },
        { label: "My",  href: "/tickets?queue=my", count: ticketsNav.mine },
        ...ticketsNav.queues.map((q) => ({
          label: q.name,
          href:  `/tickets?queue=${q.id}`,
          count: q.count,
        })),
      ]
      return { ...item, children }
    })
    .map((item) => filterByCapability(item, can))
    .filter((i): i is NavItem => i !== null)

  return (
    <aside className="sticky top-0 flex h-screen w-72 flex-col justify-between overflow-y-auto border-r border-border bg-app-panel p-4">
      <div>
        <header className="mb-8 flex items-center gap-3">
          <LogoMark small />
          <div>
            <div className="text-lg font-semibold">
              Valet<span className="text-brand-red">.red</span>
            </div>
            <div className="text-[10px] uppercase tracking-[.22em] text-app-muted">
              Support OS
            </div>
          </div>
        </header>

        <nav className="space-y-1 text-base">
          {items.map((item) => (
            <NavRow key={item.label} item={item} currentUrl={url} depth={0} />
          ))}
        </nav>
      </div>

      <footer className="space-y-3 border-t border-border pt-4">
        <div className="rounded-control border border-vip-gold/20 bg-vip-goldMute p-3 text-xs text-vip-goldSoft">
          Every user is a VIP
        </div>
        <CompanySwitcher />
        <button
          type="button"
          onClick={() => router.delete("/users/sign_out")}
          className="flex w-full items-center justify-center gap-2 rounded-control border border-border bg-white/[0.03] px-3 py-2 text-sm text-app-muted transition hover:bg-white/10 hover:text-app-text"
        >
          <LogOut size={14} strokeWidth={2} /> Sign out
        </button>
      </footer>
    </aside>
  )
}

type RowProps = { item: NavItem; currentUrl: string; depth: number }

function NavRow({ item, currentUrl, depth }: RowProps) {
  const active = item.href ? matchActive(currentUrl, item.href) : false
  const hasChildren = !!item.children?.length
  const padding = depth === 0 ? "px-3 py-2" : "px-3 py-2"

  // Top-level group with children gets the red-tinted container treatment.
  // When the group is inactive (operator isn't browsing inside it), its
  // children collapse to save vertical space and re-expand on hover —
  // clicking the top-level row still navigates because it's a real Link.
  if (depth === 0 && hasChildren) {
    const groupActive = active || item.children!.some((c) => matchActive(currentUrl, c.href ?? ""))
    return (
      <div className={`group mt-3 rounded-control border ${groupActive ? "border-brand-red/10 bg-brand-red/5" : "border-transparent"} p-2`}>
        <Link
          href={item.href ?? "#"}
          className="flex items-center justify-between rounded-control px-2 py-2 text-app-text"
        >
          <span>{item.icon} {item.label}</span>
          {hasChildren && <span className={`text-vip-gold text-2xl leading-none transition-transform ${groupActive ? "" : "group-hover:rotate-0 -rotate-90"}`}>▾</span>}
        </Link>
        {/* Hover-intent: 200ms forgiveness before the submenu collapses
            again. Eliminates the "open then close while hovering an
            option" flicker David hit. Using a wrapper that's always
            in-flow (no hidden) plus animated max-height gives a smooth
            collapse without re-flowing the page. */}
        <div className={cn(
          "grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out",
          groupActive ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0 group-hover:grid-rows-[1fr] group-hover:opacity-100 delay-100",
        )}>
          <div className="min-h-0">
            <div className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
              {item.children!.map((child) => (
                <NavRow key={child.label} item={child} currentUrl={currentUrl} depth={depth + 1} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Leaf or nested item. Top-level rows stay at full text color regardless of
  // active state — the chevron carries the "has children" / collapsed signal.
  // Nested rows still dim when inactive so the active child is obvious.
  const inactiveText = depth === 0 ? "text-app-text" : "text-app-muted"
  const className = active
    ? `rounded-control bg-white/10 ${padding} text-app-text`
    : `rounded-control hover:bg-white/5 ${padding} ${inactiveText}`

  if (item.href) {
    return (
      <Link href={item.href} className={`${className} flex items-center justify-between gap-2`}>
        <span className="truncate">{depth === 0 && item.icon ? `${item.icon} ` : ""}{item.label}</span>
        <span className="flex shrink-0 items-center gap-2">
          {typeof item.count === "number" && item.count > 0 && (
            <span className={
              active
                ? "rounded-chip border border-brand-red/40 bg-brand-red/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-red"
                : "rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted"
            }>
              {item.count}
            </span>
          )}
          {hasChildren && <span className="text-2xl leading-none">▾</span>}
        </span>
      </Link>
    )
  }

  return <div className={className}>{item.label}</div>
}

// Walks the nav tree, dropping any node whose `requiresCapability` the
// viewer lacks. A parent that DOES pass its own gate but loses every
// child to filtering is also dropped — renders as an empty group
// otherwise. A parent without a `requiresCapability` survives as long as
// it has at least one surviving child OR a usable href; otherwise it
// drops too.
function filterByCapability(item: NavItem, can: (c: string) => boolean): NavItem | null {
  if (item.requiresCapability && !can(item.requiresCapability)) return null

  const hasChildrenInput = !!item.children?.length
  if (!hasChildrenInput) return item

  const filteredChildren = item.children!
    .map((c) => filterByCapability(c, can))
    .filter((c): c is NavItem => c !== null)

  if (filteredChildren.length === 0) {
    // Parent had children defined; if all got filtered out, drop the
    // parent — empty groups are noise. The href on the parent is
    // ignored at this point because the whole reason to keep a parent
    // is the children.
    return null
  }
  return { ...item, children: filteredChildren }
}

// Active match: exact path, or path with matching query string.
function matchActive(currentUrl: string, target: string): boolean {
  if (!target || target === "#") return false
  // Strip query for path comparison; if target has a query, require full match.
  const [targetPath, targetQuery] = target.split("?")
  const [currentPath, currentQuery] = currentUrl.split("?")
  if (targetQuery) return target === currentUrl
  return currentPath === targetPath && !currentQuery
}
